/**
 * Bulk Download Reports Component
 * Allows team managers/coaches to download all player reports at once
 * Supports pagination for large player counts
 */
import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { 
  Download, AlertTriangle, CheckCircle, Users, 
  Calendar, ExternalLink, Loader2, X, FileText,
  ChevronLeft, ChevronRight, Package
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { API } from '../config/api';

export default function BulkDownloadReports({ teamName, className }) {
  const navigate = useNavigate();
  const [validating, setValidating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [validation, setValidation] = useState(null);
  const [downloadInfo, setDownloadInfo] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });
  const [downloadMode, setDownloadMode] = useState('single'); // 'single' or 'all'

  const validateDownload = async () => {
    setValidating(true);
    try {
      const token = localStorage.getItem('token');
      const params = teamName ? `?team_name=${encodeURIComponent(teamName)}` : '';
      
      // Fetch both validation and info in parallel
      const [validationRes, infoRes] = await Promise.all([
        axios.get(`${API}/reports/bulk-download/validate${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/reports/bulk-download/info${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setValidation(validationRes.data);
      setDownloadInfo(infoRes.data);
      setShowModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to validate download');
    } finally {
      setValidating(false);
    }
  };

  const downloadSinglePage = async (page = currentPage) => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (teamName) params.append('team_name', teamName);
      params.append('page', page.toString());
      params.append('page_size', '25');
      
      const response = await fetch(`${API}/reports/bulk-download?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        
        // Get filename from Content-Disposition header or generate one
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `reports_page${page}.zip`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename=(.+)/);
          if (match) filename = match[1];
        }
        
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
        
        // Get pagination info from headers
        const generatedCount = response.headers.get('X-Generated-Count');
        toast.success(`Downloaded ${generatedCount || 'reports'} reports (Page ${page})`);
        
        return true;
      } else {
        const error = await response.json();
        toast.error(error.detail?.message || error.detail || 'Failed to download reports');
        return false;
      }
    } catch (error) {
      toast.error('Failed to download reports');
      return false;
    } finally {
      setDownloading(false);
    }
  };

  const downloadAllPages = async () => {
    if (!downloadInfo) return;
    
    setDownloadMode('all');
    setDownloading(true);
    setDownloadProgress({ current: 0, total: downloadInfo.total_pages });
    
    try {
      for (let page = 1; page <= downloadInfo.total_pages; page++) {
        setDownloadProgress({ current: page, total: downloadInfo.total_pages });
        
        const token = localStorage.getItem('token');
        const params = new URLSearchParams();
        if (teamName) params.append('team_name', teamName);
        params.append('page', page.toString());
        params.append('page_size', '25');
        
        const response = await fetch(`${API}/reports/bulk-download?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `reports_${teamName?.replace(/\s+/g, '_') || 'all'}_batch${page}of${downloadInfo.total_pages}.zip`;
          a.click();
          window.URL.revokeObjectURL(url);
          
          // Small delay between downloads to prevent browser overwhelm
          if (page < downloadInfo.total_pages) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        } else {
          toast.error(`Failed to download batch ${page}`);
        }
      }
      
      toast.success(`All ${downloadInfo.total_pages} batches downloaded!`);
      setShowModal(false);
    } catch (error) {
      toast.error('Download interrupted');
    } finally {
      setDownloading(false);
      setDownloadMode('single');
      setDownloadProgress({ current: 0, total: 0 });
    }
  };

  const totalPages = downloadInfo?.total_pages || 1;

  return (
    <>
      <Button
        onClick={validateDownload}
        disabled={validating}
        className={`bg-[#ff025f] hover:bg-[#ff025f]/80 text-white ${className}`}
        data-testid="bulk-download-btn"
      >
        {validating ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <Download className="h-4 w-4 mr-2" />
        )}
        Download All Reports
      </Button>

      {/* Validation Modal */}
      {showModal && validation && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg p-6 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => !downloading && setShowModal(false)}
              className="absolute top-4 right-4 text-zinc-500 hover:text-white disabled:opacity-50"
              disabled={downloading}
            >
              <X className="h-5 w-5" />
            </button>

            <div className="flex items-center gap-3 mb-6">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                validation.can_bulk_download 
                  ? 'bg-green-500/20' 
                  : 'bg-yellow-500/20'
              }`}>
                {validation.can_bulk_download ? (
                  <CheckCircle className="h-6 w-6 text-green-400" />
                ) : (
                  <AlertTriangle className="h-6 w-6 text-yellow-400" />
                )}
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  {validation.can_bulk_download ? 'Ready to Download' : 'Action Required'}
                </h2>
                <p className="text-sm text-zinc-400">
                  {validation.total_players} player{validation.total_players !== 1 ? 's' : ''} found
                </p>
              </div>
            </div>

            {/* Summary */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-zinc-800/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle className="h-4 w-4 text-green-400" />
                  <span className="text-xs text-zinc-400">Ready</span>
                </div>
                <p className="text-2xl font-bold text-white">{validation.players_ready}</p>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="h-4 w-4 text-yellow-400" />
                  <span className="text-xs text-zinc-400">Missing DOB</span>
                </div>
                <p className="text-2xl font-bold text-white">{validation.players_missing_dob?.length || 0}</p>
              </div>
            </div>

            {/* Pagination Info for Large Downloads */}
            {downloadInfo && downloadInfo.total_pages > 1 && validation.can_bulk_download && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Package className="h-4 w-4 text-blue-400" />
                  <h3 className="text-sm font-semibold text-blue-400">Large Download Detected</h3>
                </div>
                <p className="text-sm text-zinc-300 mb-3">
                  {downloadInfo.players_with_dob} players will be split into {downloadInfo.total_pages} batches 
                  of ~25 reports each to prevent timeouts.
                </p>
                <div className="text-xs text-zinc-400 space-y-1">
                  <p>Estimated time per batch: ~{Math.round(downloadInfo.estimated_time_per_page_seconds / 60)} minutes</p>
                  <p>Total estimated time: ~{downloadInfo.estimated_total_time_minutes} minutes</p>
                </div>
              </div>
            )}

            {/* Players Missing DOB */}
            {validation.players_missing_dob?.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="h-4 w-4 text-yellow-400" />
                  <h3 className="text-sm font-semibold text-yellow-400">
                    Players need date of birth updated:
                  </h3>
                </div>
                <div className="bg-zinc-800/30 rounded-lg border border-yellow-500/20 max-h-48 overflow-y-auto">
                  {validation.players_missing_dob.map((player) => (
                    <div 
                      key={player.id}
                      className="flex items-center justify-between p-3 border-b border-zinc-700/50 last:border-0 hover:bg-zinc-800/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-zinc-700 rounded-full flex items-center justify-center">
                          <Users className="h-4 w-4 text-zinc-400" />
                        </div>
                        <span className="text-sm text-white">{player.name}</span>
                      </div>
                      <button
                        onClick={() => {
                          setShowModal(false);
                          navigate(`/child/${player.id}`);
                        }}
                        className="flex items-center gap-1 text-xs text-[#02b5ff] hover:text-[#02b5ff]/80 transition-colors"
                      >
                        <FileText className="h-3 w-3" />
                        Edit Profile
                        <ExternalLink className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-zinc-500 mt-2">
                  Click "Edit Profile" to update each player's date of birth
                </p>
              </div>
            )}

            {/* Download Progress */}
            {downloading && downloadMode === 'all' && (
              <div className="mb-6 bg-zinc-800/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-white">Downloading batches...</span>
                  <span className="text-sm text-zinc-400">
                    {downloadProgress.current} / {downloadProgress.total}
                  </span>
                </div>
                <div className="w-full bg-zinc-700 rounded-full h-2">
                  <div 
                    className="bg-[#ff025f] h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(downloadProgress.current / downloadProgress.total) * 100}%` }}
                  />
                </div>
                <p className="text-xs text-zinc-500 mt-2">
                  Please keep this window open. Files will download automatically.
                </p>
              </div>
            )}

            {/* Action Buttons */}
            {validation.can_bulk_download && downloadInfo && (
              <div className="space-y-3">
                {/* Pagination Controls for Single Page Download */}
                {totalPages > 1 && (
                  <div className="bg-zinc-800/30 rounded-lg p-4">
                    <p className="text-xs text-zinc-400 mb-3">Download by batch:</p>
                    <div className="flex items-center justify-between">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage <= 1 || downloading}
                        className="border-zinc-600"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      
                      <span className="text-sm text-white">
                        Batch {currentPage} of {totalPages}
                      </span>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage >= totalPages || downloading}
                        className="border-zinc-600"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <Button
                      onClick={() => downloadSinglePage(currentPage)}
                      disabled={downloading}
                      className="w-full mt-3 bg-zinc-700 hover:bg-zinc-600 text-white"
                    >
                      {downloading && downloadMode === 'single' ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating Batch {currentPage}...</>
                      ) : (
                        <><Download className="h-4 w-4 mr-2" /> Download Batch {currentPage}</>
                      )}
                    </Button>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowModal(false)}
                    disabled={downloading}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  
                  {totalPages > 1 ? (
                    <Button
                      onClick={downloadAllPages}
                      disabled={downloading}
                      className="flex-1 bg-[#ff025f] hover:bg-[#ff025f]/80 text-white"
                    >
                      {downloading && downloadMode === 'all' ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Downloading...</>
                      ) : (
                        <><Package className="h-4 w-4 mr-2" /> Download All {totalPages} Batches</>
                      )}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => downloadSinglePage(1)}
                      disabled={downloading}
                      className="flex-1 bg-[#ff025f] hover:bg-[#ff025f]/80 text-white"
                    >
                      {downloading ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...</>
                      ) : (
                        <><Download className="h-4 w-4 mr-2" /> Download {validation.players_ready} Reports</>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {!validation.can_bulk_download && (
              <>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled
                    className="flex-1 bg-zinc-700 text-zinc-400 cursor-not-allowed"
                  >
                    <Download className="h-4 w-4 mr-2" /> Download Reports
                  </Button>
                </div>
                <p className="text-center text-xs text-yellow-400 mt-4">
                  ⚠️ Update all player dates of birth to enable bulk download
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
